
import React, { useState } from 'react';
import type { StaffMember, Department, View, DayOfWeek } from '../../types';
import StaffModal from './StaffModal';
import StaffProfileModal from './StaffProfileModal';
import { Icons } from '../ui/Icons';
import ConfirmationModal from '../ui/ConfirmationModal';
import { useDeleteHandler } from '../../hooks/useDeleteHandler';

interface StaffManagementProps {
    staffList: StaffMember[];
    departments: Department[];
    weekStartDay: DayOfWeek;
    setView: (view: View) => void;
    onSaveStaff: (staff: StaffMember) => void;
    onDeleteStaff: (staffId: string) => void;
    onSyncAllStaff?: () => void;
}

const getInitials = (name?: string) => {
    if (!name) return '?';
    const names = name.split(' ');
    if (names.length > 1 && names[names.length - 1]) {
        return `${names[0][0]}${names[names.length - 1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
};


const StaffManagement: React.FC<StaffManagementProps> = ({ staffList, departments, weekStartDay, setView, onSaveStaff, onDeleteStaff, onSyncAllStaff }) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingStaff, setEditingStaff] = useState<StaffMember | null>(null);
    const [viewingStaff, setViewingStaff] = useState<StaffMember | null>(null);
    const [searchTerm, setSearchTerm] = useState('');
    
    const {
        itemToDelete: staffToDelete,
        isDeleting,
        initiateDelete: handleDeleteClick,
        confirmDelete: handleConfirmDelete,
        cancelDelete: handleCancelDelete,
    } = useDeleteHandler<StaffMember>({
        onDeleteAsync: async (staff) => onDeleteStaff(staff.id),
        entityName: 'Staff Member',
        getDisplayName: (staff) => staff.name,
        getItemId: (staff) => staff.id,
    });


    const handleAddStaff = () => {
        setEditingStaff(null);
        setIsModalOpen(true);
    };

    const handleEditStaff = (staff: StaffMember) => {
        setEditingStaff(staff);
        setIsModalOpen(true);
    };
    
    const handleViewProfile = (staff: StaffMember) => {
        setViewingStaff(staff);
    };

    const handleSaveStaff = (staff: StaffMember) => {
        onSaveStaff(staff);
        setIsModalOpen(false);
    };

    const departmentMap = new Map<string, Department>(departments.map(d => [d.id, d]));
    const activeStaffList = staffList.filter(s => s.status !== 'deleted');
    
    const filteredStaffList = activeStaffList.filter(staff => {
        const searchLower = searchTerm.toLowerCase();
        const primaryDepartmentId = staff.departmentIds?.[0];
        const department = primaryDepartmentId ? departmentMap.get(primaryDepartmentId) : undefined;
        
        return (
            staff.name.toLowerCase().includes(searchLower) ||
            staff.role.toLowerCase().includes(searchLower) ||
            staff.email.toLowerCase().includes(searchLower) ||
            staff.phone.toLowerCase().includes(searchLower) ||
            (department?.name.toLowerCase().includes(searchLower))
        );
    });

    return (
        <div>
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
                <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-200">Staff Roster</h2>
                <div className="flex flex-wrap gap-3 w-full md:w-auto">
                    <div className="relative flex-grow md:flex-grow-0 md:w-64">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <Icons.Search className="h-4 w-4 text-gray-400" />
                        </div>
                        <input
                            type="text"
                            placeholder="Search staff..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-slate-600 rounded-md leading-5 bg-white dark:bg-slate-800 text-gray-900 dark:text-gray-100 placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm transition-colors"
                        />
                        {searchTerm && (
                            <button
                                onClick={() => setSearchTerm('')}
                                className="absolute inset-y-0 right-0 pr-3 flex items-center"
                            >
                                <Icons.X className="h-4 w-4 text-gray-400 hover:text-gray-600" />
                            </button>
                        )}
                    </div>
                    {onSyncAllStaff && (
                        <button
                            onClick={onSyncAllStaff}
                            className="flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-md hover:bg-amber-700 transition-colors shadow-sm text-sm font-medium"
                            title="Sync all staff members to Firebase Authentication"
                        >
                            <Icons.History className="w-4 h-4" />
                            <span className="hidden sm:inline">Sync to Auth</span>
                            <span className="sm:hidden">Sync</span>
                        </button>
                    )}
                    <button
                        onClick={handleAddStaff}
                        className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors shadow-sm text-sm font-medium"
                    >
                        <Icons.UserPlus className="w-4 h-4" />
                        Add Staff
                    </button>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                {filteredStaffList.length === 0 ? (
                    <div className="col-span-full py-12 text-center bg-white dark:bg-slate-800 rounded-lg border-2 border-dashed border-gray-200 dark:border-slate-700">
                        <Icons.Users className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                            {searchTerm ? 'No matching staff members' : 'No staff members found'}
                        </h3>
                        <p className="text-gray-500 dark:text-gray-400 mt-1">
                            {searchTerm ? 'Try adjusting your search terms.' : 'Get started by adding your first staff member.'}
                        </p>
                        {!searchTerm && (
                            <button
                                onClick={handleAddStaff}
                                className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors shadow-sm"
                            >
                                <Icons.UserPlus className="w-5 h-5" />
                                Add Staff
                            </button>
                        )}
                    </div>
                ) : filteredStaffList.map(staff => {
                    const primaryDepartmentId = staff.departmentIds?.[0];
                    const department = primaryDepartmentId ? departmentMap.get(primaryDepartmentId) : undefined;
                    return (
                        <div key={staff.id} className="bg-white dark:bg-slate-800 rounded-lg shadow-md overflow-hidden transition-shadow hover:shadow-lg flex flex-col">
                            <div className="p-5 flex-grow">
                                <div className="flex items-center gap-4">
                                    <div
                                        className="w-12 h-12 rounded-full flex-shrink-0 flex items-center justify-center text-white font-bold text-lg"
                                        style={{ backgroundColor: staff.color }}
                                    >
                                        {getInitials(staff.name)}
                                    </div>
                                    <div className="flex-grow">
                                        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{staff.name}</h3>
                                        <p className="text-sm text-gray-500 dark:text-gray-400">{staff.role}</p>
                                    </div>
                                </div>
                                <div className="mt-4 pt-4 border-t border-gray-200 dark:border-slate-700 space-y-2 text-sm text-gray-600 dark:text-gray-300">
                                    {department && (
                                        <div className="flex items-center gap-2">
                                           <span
                                                className="px-2 py-0.5 text-xs font-medium rounded-full"
                                                style={{ backgroundColor: `${department.color}20`, color: department.color }}
                                            >
                                                {department.name}
                                            </span>
                                        </div>
                                    )}
                                    <div className="flex items-center gap-2 pt-1">
                                        <Icons.Mail className="w-4 h-4 text-gray-400 dark:text-gray-500 flex-shrink-0" />
                                        <span className="truncate">{staff.email}</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Icons.Phone className="w-4 h-4 text-gray-400 dark:text-gray-500 flex-shrink-0" />
                                        <span>{staff.phone}</span>
                                    </div>
                                </div>
                            </div>
                            <div className="bg-gray-50 dark:bg-slate-700/50 px-5 py-3 flex justify-end items-center gap-2">
                                <button onClick={() => handleViewProfile(staff)} className="flex items-center gap-1.5 px-3 py-1 text-sm text-gray-600 dark:text-gray-200 bg-white dark:bg-slate-600 border border-gray-300 dark:border-slate-500 rounded-md hover:bg-gray-100 dark:hover:bg-slate-500 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-indigo-500">
                                    <Icons.Eye className="w-4 h-4"/> View
                                </button>
                                <button onClick={() => handleEditStaff(staff)} className="flex items-center gap-1.5 px-3 py-1 text-sm text-indigo-600 dark:text-indigo-300 bg-indigo-50 dark:bg-indigo-900/20 border border-transparent rounded-md hover:bg-indigo-100 dark:hover:bg-indigo-900/40 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-indigo-500">
                                    <Icons.Edit className="w-4 h-4"/> Edit
                                </button>
                                <button onClick={() => handleDeleteClick(staff)} className="flex items-center gap-1.5 px-3 py-1 text-sm text-red-600 dark:text-red-300 bg-red-50 dark:bg-red-900/20 border border-transparent rounded-md hover:bg-red-100 dark:hover:bg-red-900/40 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-red-500">
                                    <Icons.Trash2 className="w-4 h-4"/> Delete
                                </button>
                            </div>
                        </div>
                    );
                })}
            </div>

            {isModalOpen && (
                <StaffModal
                    staff={editingStaff}
                    onClose={() => setIsModalOpen(false)}
                    onSave={handleSaveStaff}
                    departments={departments}
                    weekStartDay={weekStartDay}
                />
            )}
            
            {viewingStaff && (
                <StaffProfileModal
                    staff={viewingStaff}
                    departments={departments}
                    onClose={() => setViewingStaff(null)}
                />
            )}

            {staffToDelete && (
                <ConfirmationModal
                    title="Delete Staff Member"
                    message={`Are you sure you want to delete ${staffToDelete.name}? This action cannot be undone and will remove them from the schedule.`}
                    confirmButtonText="Delete"
                    onConfirm={handleConfirmDelete}
                    onCancel={handleCancelDelete}
                    isConfirming={isDeleting}
                />
            )}
        </div>
    );
};

export default StaffManagement;
